import {
  Box,
  Skeleton,
  StarRating,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import type { UserInfo } from '@redbubble/next/types';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { useEffect } from 'react';
import { ProductPageProps } from '../../types';
import styles from './ProductFeaturesReviews.module.scss';
import { useReviewSummary } from './useReviewSummary';

export const TOTAL_STARS = 5;

export const messages = defineMessages({
  reviewSummary: { defaultMessage: '{rating} ({reviewCount} reviews)' },
});

export interface ProductReviewsProps {
  onClick: () => void;
  inventoryItemId: string;
  productTypeId: string;
}

export const ProductReviews = ({
  onClick,
  inventoryItemId,
  productTypeId,
}: ProductReviewsProps) => {
  const intl = useIntl();
  const [{ locale }] = useUserInfo<UserInfo>();
  const { reviewSummary: initialReviewSummary, initialProductTypeId } =
    usePageProps<ProductPageProps>();

  const {
    reviewSummary,
    loading: loadingReviewSummary,
    refresh: refreshReviewSummary,
  } = useReviewSummary(initialProductTypeId, initialReviewSummary);

  useEffect(() => {
    refreshReviewSummary(
      {
        inventoryItemId,
        locale,
      },
      productTypeId
    );
  }, [inventoryItemId, locale, refreshReviewSummary, productTypeId]);

  if (loadingReviewSummary) {
    return (
      <Box data-testid="product-review-summary-skeleton">
        <Skeleton height={26} width={240} animated roundedCorners />
      </Box>
    );
  }

  const ratingFixed = reviewSummary?.rating.toFixed(2);

  return reviewSummary?.count ? (
    <TextLink onClick={onClick} data-testid="product-review-summary">
      <Box className={styles.reviewSummaryWrapper}>
        <StarRating outOf={TOTAL_STARS} rating={reviewSummary?.rating} />
        <Text type="display5" marginTop="xxxs">
          {intl.formatMessage(messages.reviewSummary, {
            rating: ratingFixed,
            reviewCount: reviewSummary?.count,
          })}
        </Text>
      </Box>
    </TextLink>
  ) : null;
};
